import React from 'react';
import Icon from '../../../components/AppIcon';

const ArticleCard = ({ article, onClick }) => {
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  return (
    <article 
      onClick={onClick}
      className="bg-white rounded-2xl shadow-soft border border-accent-100 overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer group"
    >
      {/* Article Image */}
      <div className="relative overflow-hidden">
        <img
          src={article?.image}
          alt={article?.title}
          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
        />
        
        {/* GEO Badge */}
        {article?.geoOptimized && (
          <div className="absolute top-4 left-4 bg-gradient-to-r from-primary to-secondary text-white px-3 py-1 rounded-full text-xs font-medium">
            <Icon name="Sparkles" size={12} className="inline mr-1" />
            AI Optimized
          </div>
        )}
        
        {/* Category Badge */}
        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-accent-700">
          {article?.category}
        </div>
      </div>

      {/* Article Content */}
      <div className="p-6">
        {/* Title */}
        <h3 className="font-heading font-semibold text-xl text-accent-800 mb-3 group-hover:text-primary transition-colors line-clamp-2">
          {article?.title}
        </h3>

        {/* Excerpt */}
        <p className="text-accent-600 text-sm mb-4 line-clamp-3">
          {article?.excerpt}
        </p>

        {/* Quick Info */}
        <div className="flex flex-wrap gap-2 mb-4">
          <div className="flex items-center space-x-1 text-xs text-accent-500">
            <Icon name="Clock" size={12} />
            <span>{article?.readTime} min read</span>
          </div>
          <div className="flex items-center space-x-1 text-xs text-accent-500">
            <Icon name="DollarSign" size={12} />
            <span>{article?.budgetRange}</span>
          </div>
          <div className="flex items-center space-x-1 text-xs text-accent-500">
            <Icon name="BarChart" size={12} />
            <span>{article?.difficulty}</span>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {article?.tags?.slice(0, 3).map(tag => (
            <span
              key={tag}
              className="px-2 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-medium"
            >
              #{tag}
            </span>
          ))}
          {article?.tags?.length > 3 && (
            <span className="px-2 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-medium">
              +{article?.tags?.length - 3} more
            </span>
          )}
        </div>

        {/* Author & Stats */}
        <div className="flex items-center justify-between pt-4 border-t border-accent-100">
          <div className="flex items-center space-x-3">
            <img
              src={article?.author?.avatar}
              alt={article?.author?.name}
              className="w-8 h-8 rounded-full object-cover"
            />
            <div>
              <div className="flex items-center space-x-1">
                <span className="font-medium text-sm text-accent-800">{article?.author?.name}</span>
                {article?.author?.isVerified && (
                  <Icon name="CheckCircle" size={14} className="text-primary" />
                )}
              </div>
              <span className="text-xs text-accent-500">{formatDate(article?.publishedAt)}</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-4 text-xs text-accent-500">
            <div className="flex items-center space-x-1">
              <Icon name="Eye" size={14} />
              <span>{formatNumber(article?.views)}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Heart" size={14} />
              <span>{formatNumber(article?.likes)}</span>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

export default ArticleCard;